import { useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import clsx from "clsx";
import { buttonVariants } from "@/components/UI/Button";
import {
  returnContractOptionLabel,
  returnFutureContractChanges,
} from "@/components/widgets/tradeFromScratch/helper/futureTicketHelper";
import {
  returnSwapTemplateChanges,
  returnSwaptionTemplateChanges,
} from "@/components/widgets/tradeFromScratch/helper/swapTemplateRule";
import { returnReferenceDataOptions } from "@/components/widgets/tradeFromScratch/helper/ticketSharedHelper";
import { isReferenceDataLoading } from "@/components/widgets/tradeFromScratch/helper/tradeFromScratchHelper";
import { useBondSecuritySearch } from "@/components/widgets/tradeFromScratch/helper/useBondSecuritySearch";
import {
  AnyTicket,
  returnDraftLegSize,
} from "@/components/widgets/tradeFromScratch/helper/draftLegAdapter";
import ContractCountInput from "@/components/widgets/tradeFromScratch/ticketFields/ContractCountInput";
import DirectionSelect from "@/components/widgets/tradeFromScratch/ticketFields/DirectionSelect";
import NominalInput, {
  isRoundedNoteShowing,
} from "@/components/widgets/tradeFromScratch/ticketFields/NominalInput";
import ReferenceDataSelect from "@/components/widgets/tradeFromScratch/ticketFields/ReferenceDataSelect";
import SwapTemplateSelect from "@/components/widgets/tradeFromScratch/ticketFields/SwapTemplateSelect";
import TicketDateInput from "@/components/widgets/tradeFromScratch/ticketFields/TicketDateInput";
import TicketTextInput from "@/components/widgets/tradeFromScratch/ticketFields/TicketTextInput";
import { BOND_SIZE_ROUNDING_STEP } from "@/lib/businessRules/minimumSizeRule";
import { findBondSecurityByIsin } from "@/store/bondSecuritiesStoreACTIONS";
import draftLegsStore from "@/store/draftLegsStore";
import { discardDraftLeg, saveDraftLeg } from "@/store/draftLegsStoreACTIONS";
import futureSecuritiesStore from "@/store/futureSecuritiesStore";
import {
  findFutureSecurityBySymbol,
  searchFutureSecurities,
} from "@/store/futureSecuritiesStoreACTIONS";
import tradeFromScratchStore, {
  ISwapTicket,
  ISwaptionTicket,
  TicketInstrument,
} from "@/store/tradeFromScratchStore";
import {
  IDraftLegRow,
  returnDraftLegBlockingReasons,
  useDraftLegHighlightedKeys,
  useDraftLegPendingKeys,
  useDraftLegTicket,
} from "./useDraftLegTicket";
import { returnSettlementHolidayCalendars } from "@/components/widgets/tradeFromScratch/helper/holidayCalendarsHelper";
import {
  DV01_INSTRUMENTS,
  NO_RISK_MESSAGE,
  returnDV01SizeChanges,
  returnTicketDV01,
  returnTicketRisk,
} from "@/components/widgets/tradeFromScratch/helper/dv01TicketHelper";
import DV01Input from "@/components/widgets/tradeFromScratch/ticketFields/DV01Input";
import { IEditableBlock } from "@/components/widgets/tradeFromScratch/helper/blockEditHelper";
import {
  isDraftLegColumnLocked,
  returnDraftLegColumnKey,
} from "./draftLegLockHelpers";
import { GridApi } from "ag-grid-community";
import { canEditBlock, openBlockEdit } from "../../blockEditActions";
import featureFlagStore from "@/store/featureFlagStore";

// LQC-2941 - the inline cells of a Trade Creation draft leg row. Each reuses the Leg
// Creation ticket field and writes straight to the draft's ticket, so both widgets share
// one set of rules. Wired in by wrapColDefsForDraftLegs.

interface IDraftLegCellProps {
  data: IDraftLegRow;
}

// Keeps a cell's input from stretching the grid row.
const CELL_CLASS = "flex h-full w-full items-center py-0.5";

// Red ring on a row cell whose field is missing while a blocked Save is hovered; amber
// when the cell has a note in the detail (a rounded nominal).
// Missing (red) beats pending (orange) beats the rounding note (amber).
// The orange lands on the control's own border (globals.css), not a frame around the cell.
export const DRAFT_PENDING_CLASS = "draftLegPending";

// LQC-3027 - a FIX-locked cell. Chrome drops hovers on disabled controls, so the cell takes
// them instead: that shows the cursor and fires the colDef's instant tooltip.
export const DRAFT_LOCKED_CLASS =
  "cursor-not-allowed [&>*]:pointer-events-none";

export function returnDraftCellClass(
  isFlagged: boolean,
  hasNote = false,
  isPending = false,
): string {
  return clsx(
    CELL_CLASS,
    isFlagged && "rounded-sm bg-red-500/15 ring-1 ring-inset ring-red-500",
    // LQC-3027 - an unsaved change on an edit, in the Edit Panel's pending orange.
    !isFlagged && isPending && DRAFT_PENDING_CLASS,
    !isFlagged &&
      !isPending &&
      hasNote &&
      "rounded-sm bg-amber-500/15 ring-1 ring-inset ring-amber-500",
  );
}

// LQC-2949 - bonds trade in 10,000s.
export function returnDraftLegRoundingStep(
  instrument: TicketInstrument,
): number | undefined {
  return instrument === TicketInstrument.Bond
    ? BOND_SIZE_ROUNDING_STEP
    : undefined;
}

// The rounded-nominal note for a draft, or null. The row is too short to show it, so the
// detail does (LEG_CREATION_PLAN C18).
export function useDraftLegRoundedFrom(data: IDraftLegRow): number | null {
  const { ticket, adapter } = useDraftLegTicket(data);
  const roundedFrom = draftLegsStore(
    (state) => state.roundedFromByDraftId[data.id] ?? null,
  );
  if (!ticket) return null;
  const size = returnDraftLegSize(ticket, adapter);
  return isRoundedNoteShowing(
    size,
    roundedFrom,
    returnDraftLegRoundingStep(data.instrument),
  )
    ? roundedFrom
    : null;
}

// Q22 - fits the Direction column; the tooltip keeps the full wording.
export const DRAFT_DIRECTION_LABELS: Record<string, string> = {
  "Buy Inflation": "Buy Infl",
  "Sell Inflation": "Sell Infl",
  "Buy Future": "Buy Fut",
  "Sell Future": "Sell Fut",
  "Buy Receiver": "Buy Rec",
  "Sell Receiver": "Sell Rec",
};

export const DraftLegDirectionCell = ({ data }: IDraftLegCellProps) => {
  const { ticket, adapter, update } = useDraftLegTicket(data);
  const highlightedKeys = useDraftLegHighlightedKeys(data);
  const pendingKeys = useDraftLegPendingKeys(data);
  if (!ticket) return null;
  const isLocked = isDraftLegColumnLocked("CreationDirectionCell", data);
  return (
    <div
      data-testid={`DraftLegDirectionCellContainer-${data.id}`}
      id={`DraftLegDirectionCellContainer-${data.id}`}
      className={clsx(
        returnDraftCellClass(
          highlightedKeys.has("direction"),
          false,
          pendingKeys.has("direction"),
        ),
        isLocked && DRAFT_LOCKED_CLASS,
      )}
    >
      <DirectionSelect
        testId={`DraftLegDirectionInput-${data.id}`}
        value={ticket.direction}
        directions={data.edit?.directions ?? adapter.directions}
        labels={DRAFT_DIRECTION_LABELS}
        isDisabled={isLocked}
        onChange={(direction) =>
          update(adapter.returnDirectionChanges(ticket, direction))
        }
      />
    </div>
  );
};

export const DraftLegSizeCell = ({ data }: IDraftLegCellProps) => {
  const { ticket, adapter, update } = useDraftLegTicket(data);
  const highlightedKeys = useDraftLegHighlightedKeys(data);
  const roundedFrom = draftLegsStore(
    (state) => state.roundedFromByDraftId[data.id] ?? null,
  );
  const setRoundedFrom = draftLegsStore((state) => state.setRoundedFrom);
  const noteRoundedFrom = useDraftLegRoundedFrom(data);
  const pendingKeys = useDraftLegPendingKeys(data);
  if (!ticket) return null;
  const testId = `DraftLegSizeInput-${data.id}`;
  return (
    <div
      data-testid={`DraftLegSizeCellContainer-${data.id}`}
      id={`DraftLegSizeCellContainer-${data.id}`}
      className={returnDraftCellClass(
        highlightedKeys.has(adapter.sizeKey),
        noteRoundedFrom !== null,
        pendingKeys.has(adapter.sizeKey),
      )}
    >
      {adapter.sizeKey === "contractCount" ? (
        <ContractCountInput
          testId={testId}
          value={returnDraftLegSize(ticket, adapter)}
          onCommit={(contractCount) => update({ contractCount })}
        />
      ) : (
        <NominalInput
          testId={testId}
          value={returnDraftLegSize(ticket, adapter)}
          onCommit={(nominal) => update({ nominal })}
          isDisabled={adapter.isSizeDisabled(ticket)}
          roundingStep={returnDraftLegRoundingStep(data.instrument)}
          roundedFrom={roundedFrom}
          onRoundedFromChange={(value) => setRoundedFrom(data.id, value)}
        />
      )}
    </div>
  );
};

interface ISecurityPickerProps extends IDraftLegCellProps {
  isDisabled: boolean;
}

const BondSecurityPicker = ({ data, isDisabled }: ISecurityPickerProps) => {
  const { ticket, adapter, update } = useDraftLegTicket(data);
  const { searchSecurityNameOptions, isLoading } = useBondSecuritySearch();
  const isin = (ticket as { isin?: string } | undefined)?.isin ?? "";
  const name =
    (ticket as { securityName?: string } | undefined)?.securityName ?? "";
  return (
    <ReferenceDataSelect
      testId={`DraftLegSecurityInput-${data.id}`}
      placeholder="Search security ..."
      selected={isin ? { label: name || isin, value: isin } : null}
      searchOptions={searchSecurityNameOptions}
      isDisabled={isDisabled}
      isLoading={isLoading}
      onSelect={(option) =>
        update(
          adapter.returnBondSelectChanges(
            (ticket ?? {}) as AnyTicket,
            option ? findBondSecurityByIsin(option.value) : undefined,
            option?.value ?? "",
          ),
        )
      }
    />
  );
};

const FutureContractPicker = ({ data, isDisabled }: ISecurityPickerProps) => {
  const { ticket, update } = useDraftLegTicket(data);
  const futureSecurities = futureSecuritiesStore(
    (state) => state.futureSecurities,
  );
  const isLoading = isReferenceDataLoading(
    futureSecuritiesStore((state) => state.futureSecuritiesStatus),
  );
  const symbol =
    (ticket as { bloombergSymbol?: string } | undefined)?.bloombergSymbol ?? "";
  const contract = findFutureSecurityBySymbol(symbol);
  const searchContractOptions = useCallback(
    (term: string, limit: number) =>
      returnReferenceDataOptions(
        searchFutureSecurities(term, limit),
        (future) => ({
          label: returnContractOptionLabel(future),
          value: future.bloombergSymbol,
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [futureSecurities],
  );
  return (
    <ReferenceDataSelect
      testId={`DraftLegSecurityInput-${data.id}`}
      placeholder="Search contract ..."
      selected={
        symbol
          ? {
              label: contract ? returnContractOptionLabel(contract) : symbol,
              value: symbol,
            }
          : null
      }
      searchOptions={searchContractOptions}
      isDisabled={isDisabled}
      isLoading={isLoading}
      onSelect={(option) =>
        update(
          returnFutureContractChanges(
            (ticket as { tradeDate?: string } | undefined)?.tradeDate ?? "",
            option ? findFutureSecurityBySymbol(option.value) : undefined,
            option?.value ?? "",
          ),
        )
      }
    />
  );
};

// Swap filters by Direction (spec F19); a swaption's underlying is unfiltered.
const SwapTemplatePicker = ({ data, isDisabled }: ISecurityPickerProps) => {
  const { ticket, update } = useDraftLegTicket(data);
  // An edit keeps the saved swap: a template only pre-fills a new one.
  if (isDisabled) {
    return (
      <span className="truncate" title={data.edit?.securityName}>
        {data.edit?.securityName}
      </span>
    );
  }
  if (data.instrument === TicketInstrument.Swaption) {
    const swaption = ticket as ISwaptionTicket;
    return (
      <SwapTemplateSelect
        testId={`DraftLegSecurityInput-${data.id}`}
        value={swaption.templateName}
        onSelect={(template) =>
          update(returnSwaptionTemplateChanges(swaption, template))
        }
      />
    );
  }
  const swap = ticket as ISwapTicket;
  return (
    <SwapTemplateSelect
      testId={`DraftLegSecurityInput-${data.id}`}
      value={swap.templateName}
      direction={swap.direction}
      onSelect={(template) => update(returnSwapTemplateChanges(swap, template))}
    />
  );
};

export const DraftLegSecurityCell = ({ data }: IDraftLegCellProps) => {
  const { ticket, adapter } = useDraftLegTicket(data);
  const highlightedKeys = useDraftLegHighlightedKeys(data);
  const pendingKeys = useDraftLegPendingKeys(data);
  if (!ticket) return null;
  const securityKey = returnDraftLegColumnKey("CreationSecurityCell", data);
  const isLocked = isDraftLegColumnLocked("CreationSecurityCell", data);
  return (
    <div
      data-testid={`DraftLegSecurityCellContainer-${data.id}`}
      id={`DraftLegSecurityCellContainer-${data.id}`}
      className={clsx(
        returnDraftCellClass(
          !!securityKey && highlightedKeys.has(securityKey),
          false,
          !!securityKey && pendingKeys.has(securityKey),
        ),
        isLocked && DRAFT_LOCKED_CLASS,
      )}
    >
      {adapter.securityKind === "bond" && (
        <BondSecurityPicker data={data} isDisabled={isLocked} />
      )}
      {adapter.securityKind === "future" && (
        <FutureContractPicker data={data} isDisabled={isLocked} />
      )}
      {adapter.securityKind === "swap" && (
        <SwapTemplatePicker data={data} isDisabled={isLocked} />
      )}
    </div>
  );
};

export const DraftLegIsinCell = ({ data }: IDraftLegCellProps) => {
  const { ticket } = useDraftLegTicket(data);
  const pendingKeys = useDraftLegPendingKeys(data);
  const isin = (ticket as { isin?: string } | undefined)?.isin ?? "";
  return (
    <div
      data-testid={`DraftLegIsinCellContainer-${data.id}`}
      id={`DraftLegIsinCellContainer-${data.id}`}
      className={clsx(
        "truncate",
        pendingKeys.has("isin") && "draftLegPendingText",
      )}
    >
      {isin}
    </div>
  );
};

// LQC-2941 (spec F3) - the Risk column on a draft: DV01 linked both ways with Size.
// Bond and Future only; other instruments show nothing until saved.
export const DraftLegRiskCell = ({ data }: IDraftLegCellProps) => {
  const { ticket, adapter, update } = useDraftLegTicket(data);
  const pendingKeys = useDraftLegPendingKeys(data);
  if (!ticket || !DV01_INSTRUMENTS.includes(data.instrument)) return null;
  const isMissingRisk = returnTicketRisk(data.instrument, ticket) === null;
  return (
    <div
      data-testid={`DraftLegRiskCellContainer-${data.id}`}
      id={`DraftLegRiskCellContainer-${data.id}`}
      className={returnDraftCellClass(
        false,
        false,
        pendingKeys.has(adapter.sizeKey),
      )}
    >
      <DV01Input
        testId={`DraftLegRiskInput-${data.id}`}
        value={returnTicketDV01(data.instrument, ticket)}
        onCommit={(dv01) =>
          update(returnDV01SizeChanges(data.instrument, ticket, dv01))
        }
        disabledReason={isMissingRisk ? NO_RISK_MESSAGE : undefined}
      />
    </div>
  );
};

export const DraftLegSettlementCell = ({ data }: IDraftLegCellProps) => {
  const { ticket, update } = useDraftLegTicket(data);
  const highlightedKeys = useDraftLegHighlightedKeys(data);
  const pendingKeys = useDraftLegPendingKeys(data);
  if (!ticket) return null;
  return (
    <div
      data-testid={`DraftLegSettlementCellContainer-${data.id}`}
      id={`DraftLegSettlementCellContainer-${data.id}`}
      className={returnDraftCellClass(
        highlightedKeys.has("settlementDate"),
        false,
        pendingKeys.has("settlementDate"),
      )}
    >
      <TicketDateInput
        testId={`DraftLegSettlementInput-${data.id}`}
        holidayCalendars={returnSettlementHolidayCalendars(
          data.instrument,
          ticket,
        )}
        showHolidayNote={false}
        value={ticket.settlementDate}
        onChange={(settlementDate) => update({ settlementDate })}
      />
    </div>
  );
};

// LQC-2941 - the External ID column (spec F3), sent as tradeDetails.transactionNumber.
export const DraftLegExternalIdCell = ({ data }: IDraftLegCellProps) => {
  const { ticket, update } = useDraftLegTicket(data);
  const pendingKeys = useDraftLegPendingKeys(data);
  if (!ticket) return null;
  const isLocked = isDraftLegColumnLocked("CreationExternalIDCell", data);
  return (
    <div
      data-testid={`DraftLegExternalIdCellContainer-${data.id}`}
      id={`DraftLegExternalIdCellContainer-${data.id}`}
      className={clsx(
        returnDraftCellClass(
          false,
          false,
          pendingKeys.has("transactionNumber"),
        ),
        isLocked && DRAFT_LOCKED_CLASS,
      )}
    >
      <TicketTextInput
        testId={`DraftLegExternalIdInput-${data.id}`}
        value={ticket.transactionNumber}
        placeholder="Optional"
        isDisabled={isLocked}
        onChange={(transactionNumber) => update({ transactionNumber })}
      />
    </div>
  );
};

export function returnMissingFieldsLabel(blockingReasons: string[]): string {
  return `Missing: ${blockingReasons.join(", ")}`;
}

// Why a draft cannot be saved yet, or [] when it can. Shared by row and detail.
function useDraftLegSaveState(data: IDraftLegRow) {
  const { ticket, adapter } = useDraftLegTicket(data);
  const isSending = tradeFromScratchStore(
    (state) => state.isSendingByTicketId[data.id] ?? false,
  );
  const blockingReasons = ticket
    ? returnDraftLegBlockingReasons(data, ticket, adapter)
    : [];
  return {
    hasTicket: !!ticket,
    isSending,
    blockingReasons,
    canSave: !!ticket && blockingReasons.length === 0 && !isSending,
  };
}

interface IDraftLegActionProps {
  data: IDraftLegRow;
  // Keeps ids unique where the same action appears on the row and in the detail.
  testIdPrefix: string;
  // The row's narrow column: tighter padding, and the Status cell says "Saving ..." instead.
  isCompact?: boolean;
}

// The Save used on the row (spec F3 "Save icon") and in the detail panel. When blocked,
// hovering it turns the detail's Missing line red.
export const DraftLegSaveButton = ({
  data,
  testIdPrefix,
  isCompact = false,
}: IDraftLegActionProps) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const { hasTicket, isSending, canSave } = useDraftLegSaveState(data);
  const setHoveredSaveDraftId = draftLegsStore(
    (state) => state.setHoveredSaveDraftId,
  );
  if (!hasTicket) return null;

  return (
    // Hover lives on the wrapper: a disabled button fires no mouse events.
    <span
      data-testid={`${testIdPrefix}SaveButtonWrapper-${data.id}`}
      id={`${testIdPrefix}SaveButtonWrapper-${data.id}`}
      className={clsx("shrink-0", !canSave && "cursor-not-allowed")}
      onMouseEnter={() => setHoveredSaveDraftId(data.id)}
      onMouseLeave={() => setHoveredSaveDraftId(null)}
    >
      <button
        data-testid={`${testIdPrefix}SaveButton-${data.id}`}
        id={`${testIdPrefix}SaveButton-${data.id}`}
        type="button"
        disabled={!canSave}
        // Same variants as the header's Select Panel (ready) and Save Trade (blocked).
        className={clsx(
          buttonVariants({
            variant: canSave
              ? "mainBlueAction"
              : "secondaryBlueActionAtTradeCreation",
          }),
          // Fixed height + leading-none so the flex centring centres the glyphs, not a 16px line box.
          "h-5 text-xs font-semibold leading-none",
          isCompact ? "px-2" : "px-3",
          // Chrome swallows hovers on a disabled button, so the wrapper would never see one.
          !canSave && "pointer-events-none",
        )}
        onClick={() =>
          saveDraftLeg({
            widgetId: data.widgetId,
            draftId: data.id,
            instrument: data.instrument,
            user,
            getAccessTokenSilently,
          })
        }
      >
        {isSending && !isCompact ? "Saving ..." : "Save"}
      </button>
    </span>
  );
};

export const DraftLegDiscardButton = ({
  data,
  testIdPrefix,
}: IDraftLegActionProps) => {
  const { isSending } = useDraftLegSaveState(data);
  return (
    <button
      data-testid={`${testIdPrefix}DiscardButton-${data.id}`}
      id={`${testIdPrefix}DiscardButton-${data.id}`}
      type="button"
      disabled={isSending}
      // An edit's ✕ only drops the changes; the saved block stays.
      title={data.edit ? "Discard changes" : "Discard leg"}
      className="shrink-0 px-1 text-xs opacity-70 transition-colors enabled:hover:text-red-600 enabled:hover:opacity-100 disabled:opacity-30 dark:enabled:hover:text-red-400"
      onClick={() => discardDraftLeg(data.widgetId, data.id)}
    >
      ✕
    </button>
  );
};

export const DraftLegStatusCell = ({ data }: IDraftLegCellProps) => {
  const { hasTicket, isSending } = useDraftLegSaveState(data);
  if (!hasTicket) return null;
  const status = isSending
    ? "Saving ..."
    : data.edit
      ? `Editing leg ${data.edit.blockId}`
      : "Unassigned (Pending)";
  return (
    <div
      data-testid={`DraftLegStatusCellContainer-${data.id}`}
      id={`DraftLegStatusCellContainer-${data.id}`}
      className="truncate text-xs italic opacity-80"
      title={status}
    >
      {status}
    </div>
  );
};

// LQC-3027 - the pencil on a saved block still Unassigned that came from the app (not FIX,
// LQC-3240), behind tradeCreationEditBlocks.
export const BlockEditCell = ({
  data,
  widgetId,
  api,
}: {
  data: IEditableBlock;
  widgetId?: string;
  api?: GridApi;
}) => {
  const { getAccessTokenSilently } = useAuth0();
  // Front-end only flag: subscribed so the pencil appears as soon as it flips.
  const isEditOn = featureFlagStore((state) => state.tradeCreationEditBlocks);
  if (!isEditOn || !widgetId || !canEditBlock(data, widgetId)) return null;
  const openEdit = () =>
    openBlockEdit(widgetId, data, getAccessTokenSilently, api);
  return (
    <div
      data-testid={`BlockEditCellContainer-${data.id}`}
      id={`BlockEditCellContainer-${data.id}`}
      // Left, lined up with the heading's pencil, as the other headings start left.
      className="flex h-full w-full items-center justify-start pl-1"
    >
      <button
        data-testid={`BlockEditButton-${data.id}`}
        id={`BlockEditButton-${data.id}`}
        type="button"
        aria-label="Edit leg"
        className="px-1 text-xs opacity-70 transition-colors hover:opacity-100"
        onClick={openEdit}
      >
        ✎
      </button>
    </div>
  );
};

// Row actions get their own columns after Status, as the Order fills do (Add / Edit).
export const DraftLegSaveCell = ({ data }: IDraftLegCellProps) => (
  <div
    data-testid={`DraftLegSaveCellContainer-${data.id}`}
    id={`DraftLegSaveCellContainer-${data.id}`}
    className="flex h-full w-full items-center justify-center"
  >
    <DraftLegSaveButton data={data} testIdPrefix="DraftLeg" isCompact />
  </div>
);

export const DraftLegDiscardCell = ({ data }: IDraftLegCellProps) => (
  <div
    data-testid={`DraftLegDiscardCellContainer-${data.id}`}
    id={`DraftLegDiscardCellContainer-${data.id}`}
    className="flex h-full w-full items-center justify-center"
  >
    <DraftLegDiscardButton data={data} testIdPrefix="DraftLeg" />
  </div>
);
